// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * The 249 officially assigned ISO 3166-1 alpha-2 country codes.
 *
 * ## Why this exists
 *
 * `intake.ts` validates `country` as exactly two characters, upper-cased,
 * because `CrossBorderDetector.resolveOrgCountry` compares it to the org's own
 * code with `===`. Both intake forms nevertheless offered a free-text
 * `<input maxLength={100}>` under a label reading "Country / Jurisdiction" —
 * copy that invites a country NAME. An operator typing "Vietnam" got
 * `400 validation_error` with an English message and nothing on the form to say
 * why. A control that cannot produce a value its own schema accepts is the
 * defect; this list is what lets the control offer only values that parse.
 *
 * ## Codes only, deliberately
 *
 * No country NAME is a literal here. Names are rendered at the point of display
 * through `Intl.DisplayNames(locale, { type: 'region' })`, so a Vietnamese
 * operator reads "Việt Nam" and an English one reads "Vietnam" from the
 * platform's own locale rather than from 249 × 16 hand-authored strings that
 * would immediately start drifting. It also keeps this file free of the
 * jurisdiction vocabulary R-46 forbids in application code: a complete,
 * country-neutral standard list is the opposite of coupling to one jurisdiction,
 * and nothing here names an instrument, an authority or a framework.
 *
 * ## Officially assigned only
 *
 * Deriving the list from CLDR at runtime was tried and rejected: CLDR answers
 * for 280 two-letter regions, which includes deprecated aliases (`AN`, `BU`,
 * `CS`, `DD`, `SU`, `YU`, `ZR`, …), exceptional reservations (`UK`),
 * macro-regions (`EU`, `EZ`, `UN`, `QO`) and test values (`XA`, `XB`, `ZZ`).
 * Offering `Pseudo-Accents` as a data subject's country is not a country list.
 *
 * Verified mechanically in `__tests__/iso-3166-1-alpha-2.test.ts`: 249 entries,
 * unique, sorted, every one resolvable as a region, and none from the
 * non-officially-assigned set above.
 */
export const ISO_3166_1_ALPHA_2 = [
    'AD',
    'AE',
    'AF',
    'AG',
    'AI',
    'AL',
    'AM',
    'AO',
    'AQ',
    'AR',
    'AS',
    'AT',
    'AU',
    'AW',
    'AX',
    'AZ',
    'BA',
    'BB',
    'BD',
    'BE',
    'BF',
    'BG',
    'BH',
    'BI',
    'BJ',
    'BL',
    'BM',
    'BN',
    'BO',
    'BQ',
    'BR',
    'BS',
    'BT',
    'BV',
    'BW',
    'BY',
    'BZ',
    'CA',
    'CC',
    'CD',
    'CF',
    'CG',
    'CH',
    'CI',
    'CK',
    'CL',
    'CM',
    'CN',
    'CO',
    'CR',
    'CU',
    'CV',
    'CW',
    'CX',
    'CY',
    'CZ',
    'DE',
    'DJ',
    'DK',
    'DM',
    'DO',
    'DZ',
    'EC',
    'EE',
    'EG',
    'EH',
    'ER',
    'ES',
    'ET',
    'FI',
    'FJ',
    'FK',
    'FM',
    'FO',
    'FR',
    'GA',
    'GB',
    'GD',
    'GE',
    'GF',
    'GG',
    'GH',
    'GI',
    'GL',
    'GM',
    'GN',
    'GP',
    'GQ',
    'GR',
    'GS',
    'GT',
    'GU',
    'GW',
    'GY',
    'HK',
    'HM',
    'HN',
    'HR',
    'HT',
    'HU',
    'ID',
    'IE',
    'IL',
    'IM',
    'IN',
    'IO',
    'IQ',
    'IR',
    'IS',
    'IT',
    'JE',
    'JM',
    'JO',
    'JP',
    'KE',
    'KG',
    'KH',
    'KI',
    'KM',
    'KN',
    'KP',
    'KR',
    'KW',
    'KY',
    'KZ',
    'LA',
    'LB',
    'LC',
    'LI',
    'LK',
    'LR',
    'LS',
    'LT',
    'LU',
    'LV',
    'LY',
    'MA',
    'MC',
    'MD',
    'ME',
    'MF',
    'MG',
    'MH',
    'MK',
    'ML',
    'MM',
    'MN',
    'MO',
    'MP',
    'MQ',
    'MR',
    'MS',
    'MT',
    'MU',
    'MV',
    'MW',
    'MX',
    'MY',
    'MZ',
    'NA',
    'NC',
    'NE',
    'NF',
    'NG',
    'NI',
    'NL',
    'NO',
    'NP',
    'NR',
    'NU',
    'NZ',
    'OM',
    'PA',
    'PE',
    'PF',
    'PG',
    'PH',
    'PK',
    'PL',
    'PM',
    'PN',
    'PR',
    'PS',
    'PT',
    'PW',
    'PY',
    'QA',
    'RE',
    'RO',
    'RS',
    'RU',
    'RW',
    'SA',
    'SB',
    'SC',
    'SD',
    'SE',
    'SG',
    'SH',
    'SI',
    'SJ',
    'SK',
    'SL',
    'SM',
    'SN',
    'SO',
    'SR',
    'SS',
    'ST',
    'SV',
    'SX',
    'SY',
    'SZ',
    'TC',
    'TD',
    'TF',
    'TG',
    'TH',
    'TJ',
    'TK',
    'TL',
    'TM',
    'TN',
    'TO',
    'TR',
    'TT',
    'TV',
    'TW',
    'TZ',
    'UA',
    'UG',
    'UM',
    'US',
    'UY',
    'UZ',
    'VA',
    'VC',
    'VE',
    'VG',
    'VI',
    'VN',
    'VU',
    'WF',
    'WS',
    'YE',
    'YT',
    'ZA',
    'ZM',
    'ZW',
];
//# sourceMappingURL=iso-3166-1-alpha-2.js.map